import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useDashboardPermissions } from '@/hooks/useDashboardPermissions';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { StatCard } from './StatCard';
import { Home, MessageSquare, Eye, Building2, UserPlus, Tag, LandPlot, Store, Clock, AlertTriangle, Trophy, Settings2, FolderKanban, Users, Megaphone, DollarSign, BarChart3, Star, Sparkles, ExternalLink, HardHat } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { MyPackageWidget } from '@/components/dashboard/MyPackageWidget';
import { WalletCard } from '@/components/dashboard/WalletCard';

export function AgencyDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { permissions: perms } = useDashboardPermissions();

  // Get agency info
  const { data: myAgency } = useQuery({
    queryKey: ['my-agency-info', user?.id],
    queryFn: async () => {
      const { data } = await supabase.from('agencies').select('id, name, slug').eq('created_by', user!.id).maybeSingle();
      return data;
    },
    enabled: !!user,
  });

  // Settings (mode + SLA)
  const { data: settings } = useQuery({
    queryKey: ['agency-dash-settings', myAgency?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from('agency_settings')
        .select('lead_assignment_mode, sla_response_minutes')
        .eq('agency_id', myAgency!.id)
        .maybeSingle();
      return data || { lead_assignment_mode: 'listing_owner', sla_response_minutes: 30 };
    },
    enabled: !!myAgency,
  });

  // Agency members (agent_profiles)
  const { data: agents } = useQuery({
    queryKey: ['agency-dash-agents', myAgency?.id],
    queryFn: async () => {
      const { data: ap } = await supabase.from('agent_profiles').select('user_id').eq('agency_id', myAgency!.id);
      const ids = (ap || []).map(a => a.user_id);
      if (!ids.length) return [];
      const { data: profs } = await supabase.from('profiles').select('user_id, full_name').in('user_id', ids);
      const map = Object.fromEntries((profs || []).map(p => [p.user_id, p.full_name]));
      return ids.map(id => ({ user_id: id, full_name: map[id] || 'Agent' }));
    },
    enabled: !!myAgency,
  });

  // Agency-wide listings (every property/plot owned by any agent in the agency)
  const { data: stats, isLoading } = useQuery({
    queryKey: ['agency-dashboard-stats-v2', myAgency?.id, agents?.length],
    queryFn: async () => {
      const ids = (agents || []).map(a => a.user_id);
      if (!ids.length) {
        return { totalListings: 0, activeListings: 0, teamAgents: 0 };
      }
      const [totalRes, activeRes] = await Promise.all([
        supabase.from('properties').select('*', { count: 'exact', head: true }).in('user_id', ids),
        supabase.from('properties').select('*', { count: 'exact', head: true }).in('user_id', ids).eq('status', 'active'),
      ]);
      return {
        totalListings: totalRes.count || 0,
        activeListings: activeRes.count || 0,
        teamAgents: ids.length,
      };
    },
    enabled: !!myAgency && !!agents,
  });

  // All agency leads — RLS now scopes us to leads on agency listings
  const { data: leads } = useQuery({
    queryKey: ['agency-dashboard-leads', myAgency?.id, agents?.length],
    queryFn: async () => {
      const ids = (agents || []).map(a => a.user_id);
      if (!ids.length) return [];
      const { data } = await supabase
        .from('leads')
        .select('id, name, phone, status, created_at, claimed_at, assigned_to, agent_id')
        .or(`agent_id.in.(${ids.join(',')}),assigned_to.in.(${ids.join(',')})`)
        .order('created_at', { ascending: false })
        .limit(500);
      return data || [];
    },
    enabled: !!myAgency && !!agents,
  });

  const slaMinutes = settings?.sla_response_minutes ?? 30;
  const computed = useMemo(() => {
    const all = leads || [];
    const now = Date.now();
    const last24 = now - 24 * 3600 * 1000;
    const last30d = now - 30 * 86400 * 1000;

    const newLeads24h = all.filter(l => new Date(l.created_at).getTime() >= last24).length;
    const isUnclaimed = (l: any) => !l.claimed_at && !['contacted', 'converted', 'closed'].includes(l.status);
    const pastSla = all.filter(l => isUnclaimed(l) && now > new Date(l.created_at).getTime() + slaMinutes * 60 * 1000).length;
    // Hard 24h escalation — needs admin reassignment
    const past24hEscalation = all.filter(l => isUnclaimed(l) && now > new Date(l.created_at).getTime() + 24 * 3600 * 1000).length;

    const claimed30d = all.filter(l => l.claimed_at && new Date(l.created_at).getTime() >= last30d);
    const avgResponseMins = claimed30d.length
      ? Math.round(
          claimed30d.reduce((sum, l) => sum + (new Date(l.claimed_at!).getTime() - new Date(l.created_at).getTime()), 0)
          / claimed30d.length / 60000
        )
      : null;

    // Top performer (most converted leads in 30d)
    const conversions: Record<string, number> = {};
    all
      .filter(l => l.status === 'converted' && new Date(l.created_at).getTime() >= last30d)
      .forEach(l => {
        const id = l.assigned_to || l.agent_id;
        if (id) conversions[id] = (conversions[id] || 0) + 1;
      });
    const topId = Object.entries(conversions).sort((a, b) => b[1] - a[1])[0]?.[0];
    const top = topId ? agents?.find(a => a.user_id === topId) : null;

    return { newLeads24h, pastSla, past24hEscalation, avgResponseMins, totalLeads: all.length, top, topCount: topId ? conversions[topId] : 0 };
  }, [leads, slaMinutes, agents]);

  const recentLeads = (leads || []).slice(0, 5);

  const statusColors: Record<string, string> = {
    new: 'bg-primary', contacted: 'bg-amber-500', converted: 'bg-emerald-500', closed: 'bg-muted-foreground',
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-28 rounded-2xl" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display text-xl font-bold">Agency Dashboard</h2>
          <p className="text-sm text-muted-foreground mt-1">
            {myAgency?.name ? `${myAgency.name} · ` : ''}
            Routing: <span className="capitalize font-medium text-foreground">{(settings?.lead_assignment_mode || 'listing_owner').replace('_', ' ')}</span>
            {' · '}SLA <span className="font-medium text-foreground">{slaMinutes}m</span>
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {myAgency?.slug && (
            <Button variant="outline" size="sm" className="gap-2" onClick={() => navigate(`/agencies/${myAgency.slug}`)}>
              <ExternalLink className="h-4 w-4" /> View Public Profile
            </Button>
          )}
          <Button variant="outline" size="sm" className="gap-2" onClick={() => navigate('/dashboard/agency-settings')}>
            <Settings2 className="h-4 w-4" /> Routing Settings
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <MyPackageWidget />
        <WalletCard />
      </div>
      {/* Stat Cards — agency-wide */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard value={stats?.activeListings || 0} label="Active Listings" variant="rent" progress={stats?.totalListings ? Math.round(((stats?.activeListings || 0) / stats.totalListings) * 100) : 0} />
        <StatCard value={computed.newLeads24h} label="New Leads (24h)" variant="customer" progress={Math.min(computed.newLeads24h * 10, 100)} />
        <StatCard value={computed.avgResponseMins ?? 0} label="Avg Response (min)" variant="city" progress={computed.avgResponseMins != null ? Math.max(0, 100 - Math.min(computed.avgResponseMins, 100)) : 0} />
        <StatCard value={stats?.teamAgents || 0} label="Team Agents" variant="sale" progress={Math.min((stats?.teamAgents || 0) * 20, 100)} />
      </div>

      {/* 24h Escalation Banner — needs admin reassignment */}
      {computed.past24hEscalation > 0 && (
        <div className="flex items-center gap-3 p-4 rounded-2xl bg-foreground text-background">
          <AlertTriangle className="h-5 w-5" />
          <div className="flex-1">
            <div className="font-semibold text-sm">{computed.past24hEscalation} lead{computed.past24hEscalation > 1 ? 's' : ''} past 24h — awaiting reassignment</div>
            <div className="text-xs opacity-80">Original agents did not respond within 24h. Reassign to keep customers engaged.</div>
          </div>
          <Button size="sm" variant="secondary" onClick={() => navigate('/dashboard/agency-inbox?sla=past_24h')}>Reassign Now</Button>
        </div>
      )}

      {/* SLA Breach Banner (soft SLA) */}
      {computed.pastSla > 0 && (
        <div className="flex items-center gap-3 p-4 rounded-2xl bg-destructive/10 text-destructive">
          <AlertTriangle className="h-5 w-5" />
          <div className="flex-1">
            <div className="font-semibold text-sm">{computed.pastSla} lead{computed.pastSla > 1 ? 's' : ''} past SLA</div>
            <div className="text-xs">Open the Lead Inbox to reassign or contact them now.</div>
          </div>
          <Button size="sm" variant="outline" onClick={() => navigate('/dashboard/agency-inbox')}>Open Inbox</Button>
        </div>
      )}

      {/* Top Performer */}
      {computed.top && (
        <Card className="rounded-2xl border-0 shadow-sm bg-gradient-to-r from-amber-500/5 to-amber-500/10">
          <CardContent className="p-4 flex items-center gap-3">
            <Trophy className="h-7 w-7 text-amber-500" />
            <div className="flex-1">
              <div className="text-sm font-semibold">Top Performer (30d)</div>
              <div className="text-xs text-muted-foreground"><strong>{computed.top.full_name}</strong> with {computed.topCount} converted lead{computed.topCount > 1 ? 's' : ''}</div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid lg:grid-cols-2 gap-4">
        {/* Recent Leads */}
        <Card className="rounded-2xl border-0 shadow-sm">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-sm flex items-center gap-2">
                <MessageSquare className="h-4 w-4 text-primary" /> Recent Inquiries
              </h3>
              <button onClick={() => navigate('/dashboard/agency-inbox')} className="text-xs text-primary font-semibold hover:underline">Open Inbox</button>
            </div>
            {recentLeads && recentLeads.length > 0 ? (
              <div className="space-y-3">
                {recentLeads.map((lead: any) => (
                  <div key={lead.id} className="flex items-center gap-3 p-3 rounded-xl bg-muted/50">
                    <div className={`h-2.5 w-2.5 rounded-full ${statusColors[lead.status] || 'bg-muted-foreground'}`} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{lead.name}</div>
                      <div className="text-xs text-muted-foreground">{lead.phone}</div>
                    </div>
                    <div className="text-[10px] text-muted-foreground flex items-center gap-1">
                      {!lead.claimed_at && <Clock className="h-3 w-3" />}
                      {new Date(lead.created_at).toLocaleDateString()}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-8">No inquiries yet.</p>
            )}
          </CardContent>
        </Card>

        {/* Manage Members Card */}
        {perms.canManageAgencyMembers && (
          <Card className="rounded-2xl border-0 shadow-sm">
            <CardContent className="p-5">
              <h3 className="font-semibold text-sm mb-3 flex items-center gap-2">
                <UserPlus className="h-4 w-4 text-primary" /> Team Members
              </h3>
              <p className="text-xs text-muted-foreground mb-4">
                Invite agents via secure email link. Manage permissions, suspend or remove members anytime.
              </p>
              <Button onClick={() => navigate('/dashboard/members')} className="w-full gap-2">
                <UserPlus className="h-4 w-4" /> Manage Members
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Quick Actions — gated */}
        <Card className="rounded-2xl border-0 shadow-sm">
          <CardContent className="p-5">
            <h3 className="font-semibold text-sm mb-4 flex items-center gap-2">
              <Building2 className="h-4 w-4 text-primary" /> Quick Actions
            </h3>
            <div className="grid grid-cols-2 gap-3">
              {[
                { label: 'Add Property', url: '/dashboard/properties/new', icon: Tag, color: 'bg-primary/10 text-primary', show: perms.canAddProperty },
                { label: 'Add Plot', url: '/dashboard/plots/new', icon: LandPlot, color: 'bg-emerald-500/10 text-emerald-600', show: perms.canAddPlot },
                { label: 'Add Commercial', url: '/dashboard/commercial/new', icon: Store, color: 'bg-amber-500/10 text-amber-600', show: perms.canAddCommercial },
                { label: 'Add Project', url: '/dashboard/projects/new', icon: Building2, color: 'bg-indigo-500/10 text-indigo-600', show: perms.canAddProject },
                { label: 'My Projects', url: '/dashboard/my-projects', icon: FolderKanban, color: 'bg-indigo-500/10 text-indigo-600', show: perms.canViewMyProjects },
                { label: 'Inventory', url: '/dashboard/inventory', icon: HardHat, color: 'bg-orange-500/10 text-orange-600', show: perms.canManageBookings },
                { label: 'View Listings', url: '/dashboard/listings', icon: Eye, color: 'bg-accent/10 text-accent', show: perms.canViewMyListings },
                { label: 'Lead Inbox', url: '/dashboard/agency-inbox', icon: MessageSquare, color: 'bg-primary/10 text-primary', show: perms.canManageAgencyMembers },
                { label: 'Routing Settings', url: '/dashboard/agency-settings', icon: Settings2, color: 'bg-muted-foreground/10 text-muted-foreground', show: perms.canManageAgencyMembers },
                { label: 'Lead Pipeline', url: '/dashboard/pipeline', icon: FolderKanban, color: 'bg-indigo-500/10 text-indigo-600', show: perms.canViewLeadPipeline },
                { label: 'Clients', url: '/dashboard/clients', icon: Users, color: 'bg-blue-500/10 text-blue-600', show: perms.canManageClients },
                { label: 'Lead Assignment', url: '/dashboard/lead-assignment', icon: UserPlus, color: 'bg-primary/10 text-primary', show: perms.canAssignLeads },
                { label: 'Analytics', url: '/dashboard/analytics', icon: BarChart3, color: 'bg-emerald-500/10 text-emerald-600', show: perms.canViewAnalytics },
                { label: 'Reviews', url: '/dashboard/reviews', icon: Star, color: 'bg-amber-500/10 text-amber-600', show: perms.canViewReviews },
                { label: 'My Banner Ads', url: '/dashboard/my-banners', icon: Megaphone, color: 'bg-pink-500/10 text-pink-600', show: perms.canManageMyBanners },
                { label: 'Boost / Featured', url: '/dashboard/listings', icon: Sparkles, color: 'bg-amber-500/10 text-amber-600', show: true },
                { label: 'My Invoices', url: '/dashboard/my-invoices', icon: DollarSign, color: 'bg-emerald-500/10 text-emerald-600', show: true },
              ].filter(i => i.show).map(item => (
                <button
                  key={item.label}
                  onClick={() => navigate(item.url)}
                  className="flex items-center gap-3 p-4 rounded-xl bg-muted/50 hover:bg-muted transition-colors text-left"
                >
                  <div className={`h-10 w-10 rounded-lg ${item.color} flex items-center justify-center`}>
                    <item.icon className="h-5 w-5" />
                  </div>
                  <span className="text-sm font-medium">{item.label}</span>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
